import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../App.css";

interface Order {
  _id: string;
  name: string;
  phone: string;
  whatsapp: string;
  state: string;
  address: string;
  deliveryAvailability: string;
  paymentMethod: string;
  quantity: number;          // new field
  amount: number;
  status: string;
  orderDate: string;
  deliveryDate?: string;
  notes?: string;
}

interface Stats {
  totalOrders: number;
  pendingOrders: number;
  processingOrders: number;
  deliveredOrders: number;
  cancelledOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // For update modal
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateStatus, setUpdateStatus] = useState("");
  const [updateNotes, setUpdateNotes] = useState("");
  const [updating, setUpdating] = useState(false);

  // For view modal
  const [viewOrder, setViewOrder] = useState<Order | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
    }
  }, [navigate]);

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");

      const [ordersResponse, statsResponse] = await Promise.all([
        axios.get("https://supergro-backend.onrender.com/api/admin/orders", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get("https://supergro-backend.onrender.com/api/admin/stats", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (ordersResponse.data.success) {
        setOrders(ordersResponse.data.orders);
      }

      if (statsResponse.data.success) {
        setStats(statsResponse.data.stats);
      }
    } catch (error: any) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        navigate("/admin/login");
      }
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter orders
  const filteredOrders = orders.filter((order) => {
    if (filter !== "all" && order.status !== filter) return false;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        order.name.toLowerCase().includes(term) ||
        order.phone.includes(term) ||
        order.whatsapp.includes(term) ||
        order.state.toLowerCase().includes(term) ||
        order._id.toLowerCase().includes(term)
      );
    }

    return true;
  });

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    navigate("/admin/login");
  };

  // Handle status update
  const handleUpdateStatus = async () => {
    if (!selectedOrder || !updateStatus) return;

    try {
      setUpdating(true);
      const token = localStorage.getItem("adminToken");

      const response = await axios.patch(
        `https://supergro-backend.onrender.com/api/admin/orders/${selectedOrder._id}`,
        { status: updateStatus, notes: updateNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setOrders(
          orders.map((order) =>
            order._id === selectedOrder._id ? response.data.order : order
          )
        );
        fetchData(); // refresh stats
        setShowUpdateModal(false);
        setSelectedOrder(null);
        setUpdateStatus("");
        setUpdateNotes("");
      }
    } catch (error) {
      console.error("Update error:", error);
      alert("Failed to update order status");
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete order
  const handleDeleteOrder = async (orderId: string) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;

    try {
      const token = localStorage.getItem("adminToken");

      const response = await axios.delete(
        `https://supergro-backend.onrender.com/api/admin/orders/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setOrders(orders.filter((order) => order._id !== orderId));
        fetchData(); // refresh stats
        alert("Order deleted successfully");
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert("Failed to delete order");
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-NG", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString()}`;
  };

  // Status badge
  const StatusBadge = ({ status }: { status: string }) => {
    const colors: Record<string, string> = {
      pending: "#ff9800",
      processing: "#2196f3",
      delivered: "#4caf50",
      cancelled: "#f44336",
    };

    return (
      <span
        style={{
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: "bold",
          color: "white",
          backgroundColor: colors[status] || "#999",
        }}
      >
        {status.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#f5f5f5",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "50px",
              height: "50px",
              border: "5px solid #f3f3f3",
              borderTop: "5px solid #2E8B57",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          ></div>
          <p style={{ color: "#666" }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="admin-dashboard"
      style={{ minHeight: "100vh", background: "#f5f5f5" }}
    >
      {/* Header */}
      <header
        style={{
          background: "white",
          padding: "20px 0",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <div
          className="container"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h1
              style={{
                color: "#2E8B57",
                margin: 0,
                fontSize: "24px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <span>📊</span> Admin Dashboard
            </h1>
            <p style={{ color: "#666", margin: "5px 0 0", fontSize: "14px" }}>
              The Peters Agriculture - Order Management
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <button
              onClick={() => fetchData()}
              style={{
                padding: "8px 16px",
                background: "#2E8B57",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span>🔄</span> Refresh
            </button>
            <button
              onClick={handleLogout}
              style={{
                padding: "8px 16px",
                background: "#f44336",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Stats Section */}
      <section style={{ padding: "40px 0" }}>
        <div className="container">
          {stats && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "20px",
                marginBottom: "40px",
              }}
            >
              <div
                style={{
                  background: "white",
                  padding: "25px",
                  borderRadius: "10px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  textAlign: "center",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 10px",
                    color: "#666",
                    fontSize: "14px",
                  }}
                >
                  Total Orders
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "32px",
                    fontWeight: "bold",
                    color: "#2E8B57",
                  }}
                >
                  {stats.totalOrders}
                </p>
              </div>

              <div
                style={{
                  background: "white",
                  padding: "25px",
                  borderRadius: "10px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  textAlign: "center",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 10px",
                    color: "#666",
                    fontSize: "14px",
                  }}
                >
                  Pending
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "32px",
                    fontWeight: "bold",
                    color: "#ff9800",
                  }}
                >
                  {stats.pendingOrders}
                </p>
              </div>

              <div
                style={{
                  background: "white",
                  padding: "25px",
                  borderRadius: "10px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  textAlign: "center",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 10px",
                    color: "#666",
                    fontSize: "14px",
                  }}
                >
                  Delivered
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "32px",
                    fontWeight: "bold",
                    color: "#4caf50",
                  }}
                >
                  {stats.deliveredOrders}
                </p>
              </div>

              <div
                style={{
                  background: "white",
                  padding: "25px",
                  borderRadius: "10px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  textAlign: "center",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 10px",
                    color: "#666",
                    fontSize: "14px",
                  }}
                >
                  Revenue
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "32px",
                    fontWeight: "bold",
                    color: "#2196f3",
                  }}
                >
                  {formatCurrency(stats.totalRevenue)}
                </p>
              </div>
            </div>
          )}

          {/* Filters and Search */}
          <div
            style={{
              background: "white",
              padding: "20px",
              borderRadius: "10px",
              marginBottom: "30px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            }}
          >
            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder="Search orders by name, phone, or state..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 15px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <div>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  style={{
                    padding: "12px 15px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                    background: "white",
                    minWidth: "150px",
                  }}
                >
                  <option value="all">All Orders</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Orders Table */}
          <div
            style={{
              background: "white",
              borderRadius: "10px",
              overflow: "hidden",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8f9fa" }}>
                    <th style={{ padding: "15px", textAlign: "left", fontWeight: "600", color: "#333" }}>Order ID</th>
                    <th style={{ padding: "15px", textAlign: "left", fontWeight: "600", color: "#333" }}>Customer</th>
                    <th style={{ padding: "15px", textAlign: "left", fontWeight: "600", color: "#333" }}>Contact</th>
                    <th style={{ padding: "15px", textAlign: "left", fontWeight: "600", color: "#333" }}>Qty</th>
                    <th style={{ padding: "15px", textAlign: "left", fontWeight: "600", color: "#333" }}>Amount</th>
                    <th style={{ padding: "15px", textAlign: "left", fontWeight: "600", color: "#333" }}>Status</th>
                    <th style={{ padding: "15px", textAlign: "left", fontWeight: "600", color: "#333" }}>Date</th>
                    <th style={{ padding: "15px", textAlign: "left", fontWeight: "600", color: "#333" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: "40px", textAlign: "center", color: "#666" }}>
                        No orders found
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order) => (
                      <tr key={order._id} style={{ borderBottom: "1px solid #eee" }}>
                        <td style={{ padding: "15px" }}>
                          <div style={{ fontSize: "12px", color: "#666" }}>{order._id.slice(-8)}</div>
                        </td>
                        <td style={{ padding: "15px" }}>
                          <div style={{ fontWeight: "500" }}>{order.name}</div>
                          <div style={{ fontSize: "12px", color: "#666" }}>{order.state}</div>
                        </td>
                        <td style={{ padding: "15px" }}>
                          <div>{order.phone}</div>
                          <div style={{ fontSize: "12px", color: "#666" }}>WhatsApp: {order.whatsapp}</div>
                        </td>
                        <td style={{ padding: "15px" }}>
                          <div style={{ fontWeight: "500", textAlign: "center" }}>{order.quantity}</div>
                        </td>
                        <td style={{ padding: "15px" }}>
                          <div style={{ fontWeight: "bold", color: "#2E8B57" }}>{formatCurrency(order.amount)}</div>
                        </td>
                        <td style={{ padding: "15px" }}>
                          <StatusBadge status={order.status} />
                        </td>
                        <td style={{ padding: "15px" }}>
                          <div style={{ fontSize: "14px" }}>{formatDate(order.orderDate)}</div>
                        </td>
                        <td style={{ padding: "15px" }}>
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            <button
                              onClick={() => {
                                setViewOrder(order);
                                setShowViewModal(true);
                              }}
                              style={{
                                padding: "6px 12px",
                                background: "#4caf50",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                              }}
                            >
                              View
                            </button>
                            <button
                              onClick={() => {
                                setSelectedOrder(order);
                                setUpdateStatus(order.status);
                                setUpdateNotes(order.notes || "");
                                setShowUpdateModal(true);
                              }}
                              style={{
                                padding: "6px 12px",
                                background: "#2196f3",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                              }}
                            >
                              Update
                            </button>
                            <button
                              onClick={() => handleDeleteOrder(order._id)}
                              style={{
                                padding: "6px 12px",
                                background: "#f44336",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* View Order Modal */}
      {showViewModal && viewOrder && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowViewModal(false)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "10px",
              padding: "30px",
              width: "100%",
              maxWidth: "600px",
              maxHeight: "90vh",
              overflowY: "auto",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, color: "#2E8B57", marginBottom: "20px" }}>
              Order Details
            </h2>

            <div style={{ marginBottom: "20px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                <div>
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Order ID</div>
                  <div style={{ fontWeight: "500" }}>{viewOrder._id}</div>
                </div>
                <div>
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Order Date</div>
                  <div>{formatDate(viewOrder.orderDate)}</div>
                </div>
                <div>
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Status</div>
                  <StatusBadge status={viewOrder.status} />
                </div>
                <div>
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Payment Method</div>
                  <div style={{ textTransform: "capitalize" }}>{viewOrder.paymentMethod === "paystack" ? "Online" : "Pay on Delivery"}</div>
                </div>
              </div>
            </div>

            <hr style={{ border: "none", borderTop: "1px solid #eee", margin: "20px 0" }} />

            <h3 style={{ margin: "0 0 15px", fontSize: "18px", color: "#333" }}>Customer Information</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
              <div>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Full Name</div>
                <div style={{ fontWeight: "500" }}>{viewOrder.name}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>State</div>
                <div>{viewOrder.state}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Phone Number</div>
                <div>{viewOrder.phone}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>WhatsApp Number</div>
                <div>{viewOrder.whatsapp}</div>
              </div>
            </div>

            <div style={{ marginTop: "15px" }}>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Full Delivery Address</div>
              <div style={{ background: "#f9f9f9", padding: "12px", borderRadius: "6px" }}>
                {viewOrder.address}
              </div>
            </div>

            <div style={{ marginTop: "15px" }}>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Delivery Availability</div>
              <div>{viewOrder.deliveryAvailability}</div>
            </div>

            <hr style={{ border: "none", borderTop: "1px solid #eee", margin: "20px 0" }} />

            <h3 style={{ margin: "0 0 15px", fontSize: "18px", color: "#333" }}>Order Summary</h3>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: "14px" }}>
                  <strong>Product:</strong> Super Gro 5L
                </div>
                <div style={{ fontSize: "14px", marginTop: "5px" }}>
                  <strong>Quantity:</strong> {viewOrder.quantity}
                </div>
              </div>
              <div style={{ fontSize: "20px", fontWeight: "bold", color: "#2E8B57" }}>
                {formatCurrency(viewOrder.amount)}
              </div>
            </div>

            {viewOrder.notes && (
              <div style={{ marginTop: "20px" }}>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Notes</div>
                <div style={{ background: "#f9f9f9", padding: "12px", borderRadius: "6px" }}>
                  {viewOrder.notes}
                </div>
              </div>
            )}

            <div style={{ marginTop: "30px", textAlign: "right" }}>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  padding: "10px 20px",
                  background: "#666",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showUpdateModal && selectedOrder && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "10px",
              padding: "30px",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <h2 style={{ marginTop: 0, color: "#2E8B57" }}>Update Order Status</h2>

            <div style={{ marginBottom: "20px" }}>
              <div style={{ marginBottom: "15px" }}>
                <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>Customer</div>
                <div style={{ fontWeight: "500" }}>{selectedOrder.name}</div>
              </div>

              <div style={{ marginBottom: "15px" }}>
                <div style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}>Current Status</div>
                <StatusBadge status={selectedOrder.status} />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Update Status
                </label>
                <select
                  value={updateStatus}
                  onChange={(e) => setUpdateStatus(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                  disabled={updating}
                >
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Notes (Optional)
                </label>
                <textarea
                  value={updateNotes}
                  onChange={(e) => setUpdateNotes(e.target.value)}
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                    resize: "vertical",
                  }}
                  disabled={updating}
                  placeholder="Add any notes about this order..."
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  setShowUpdateModal(false);
                  setSelectedOrder(null);
                  setUpdateStatus("");
                  setUpdateNotes("");
                }}
                disabled={updating}
                style={{
                  padding: "10px 20px",
                  background: "#666",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateStatus}
                disabled={updating || !updateStatus}
                style={{
                  padding: "10px 20px",
                  background: "#2E8B57",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: updating ? "not-allowed" : "pointer",
                  opacity: updating ? 0.7 : 1,
                }}
              >
                {updating ? "Updating..." : "Update Status"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
